import React, { useEffect, useState } from 'react';
import SearchableSelect from '../common/SearchableSelect';
import { loadProjectOptions } from './projectOptionsCache';
import { ProjectService } from '../../services/project.service';
import { ProjectOption } from '../../interfaces/project';

export { loadProjectOptions, resetProjectOptionsCache } from './projectOptionsCache';

export const projectLabel = (p: ProjectOption): string =>
    `${p.code} ${p.name}${p.status === 'ON_HOLD' ? ' (on hold)' : ''}`;

/** Label of a tagged project from a DTO's code / name (resolved server-side, deleted projects included). */
export const projectTagLabel = (code?: string | null, name?: string | null): string =>
    code ? `${code}${name ? ` ${name}` : ''}` : '—';

export function useProjectOptions(institution: string | null | undefined): ProjectOption[] {
    const [options, setOptions] = useState<ProjectOption[]>([]);
    useEffect(() => {
        let live = true;
        if (!institution) {
            setOptions([]);
            return () => { live = false; };
        }
        loadProjectOptions(institution).then(o => { if (live) setOptions(o); });
        return () => { live = false; };
    }, [institution]);
    return options;
}

/**
 * P1-A tail: the invoice picker's options — projects of {@code customerId} plus those with no customer. Empty until a
 * customer is chosen; a failed read leaves the list empty (the field is optional).
 */
export function useCustomerProjectOptions(customerId: string | null | undefined): { options: ProjectOption[]; loaded: boolean } {
    const [state, setState] = useState<{ options: ProjectOption[]; loaded: boolean }>({ options: [], loaded: false });
    useEffect(() => {
        let live = true;
        setState({ options: [], loaded: false });
        if (!customerId) {
            return () => { live = false; };
        }
        Promise.resolve(ProjectService.optionsForCustomer(customerId))
            .then(o => { if (live) setState({ options: o, loaded: true }); })
            .catch(() => { if (live) setState({ options: [], loaded: true }); });
        return () => { live = false; };
    }, [customerId]);
    return state;
}

interface Props {
    value: string | null | undefined;
    onChange: (id: string) => void;
    options: ProjectOption[];
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    /** A stored tag that is no longer in the picker (closed, or not staff-taggable) still shows its label. */
    currentLabel?: string | null;
    'data-testid'?: string;
    'data-help'?: string;
}

/** P1-A project picker: the app's SearchableSelect over the options endpoint. */
const ProjectSelect: React.FC<Props> = ({ value, onChange, options, label, placeholder, size, disabled, currentLabel, ...rest }) => {
    const merged = value && currentLabel && !options.some(o => o.id === value)
        ? [...options, { id: value, code: currentLabel, name: '', status: 'CLOSED' as const }]
        : options;
    return (
        <SearchableSelect<ProjectOption>
            options={merged}
            value={value ?? ''}
            onChange={id => onChange(id)}
            getOptionLabel={o => (o.name ? projectLabel(o) : o.code)}
            label={label}
            placeholder={placeholder ?? '(no project)'}
            size={size}
            disabled={disabled}
            data-testid={rest['data-testid']}
            {...(rest['data-help'] ? { ["data-help"]: rest['data-help'] } : {})}
        />
    );
};

export default ProjectSelect;
